/* eslint-disable @typescript-eslint/no-deprecated */
import React, { useCallback, useEffect, useState } from 'react';
import { TestRecordingWidget } from '@components/Diagnostics/TestRecordingWidget';
import { StorageManagementCard } from '@components/StorageManagement';
import { useAccessToken } from '@features/user';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  LinearProgress,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import APIService from '@services/APIService';

interface RecordingTestStep {
  id: string;
  label: string;
  status: 'pending' | 'running' | 'success' | 'error';
  duration?: number;
  error?: string;
  details?: Record<string, unknown>;
}

interface RecordingTestResults {
  success: boolean;
  steps: RecordingTestStep[];
  timestamp: string;
}

interface DiagnosticData {
  // Browser Information
  browser: {
    userAgent: string;
    platform: string;
    language: string;
    languages: string[];
    cookiesEnabled: boolean;
    onLine: boolean;
    vendor: string;
    appName: string;
    appVersion: string;
    product: string;
  };

  // Screen Information
  screen: {
    width: number;
    height: number;
    availWidth: number;
    availHeight: number;
    colorDepth: number;
    pixelDepth: number;
    orientation?: string;
    devicePixelRatio: number;
  };

  // Window Information
  window: {
    innerWidth: number;
    innerHeight: number;
    outerWidth: number;
    outerHeight: number;
    scrollX: number;
    scrollY: number;
  };

  // Network Information
  network: {
    type?: string;
    effectiveType?: string;
    downlink?: number;
    downlinkMax?: number;
    rtt?: number;
    saveData?: boolean;
  };

  // Media Capabilities
  media: {
    audioInputDevices: MediaDeviceInfo[];
    audioOutputDevices: MediaDeviceInfo[];
    videoInputDevices: MediaDeviceInfo[];
    mediaPermissions: {
      audio: PermissionState | null;
      video: PermissionState | null;
    };
    supportedAudioCodecs: string[];
    supportedVideoCodecs: string[];
  };

  // System Information
  system: {
    hardwareConcurrency: number;
    deviceMemory?: number;
    maxTouchPoints: number;
    timezone: string;
    timezoneOffset: number;
    dateTime: string;
  };

  // Storage Information
  storage: {
    localStorage: {
      available: boolean;
      currentUsage?: number;
      quota?: number;
      usagePercent?: number;
      keys: string[];
    };
    sessionStorage: {
      available: boolean;
      currentUsage?: number;
      quota?: number;
      usagePercent?: number;
      keys: string[];
    };
    indexedDB: {
      available: boolean;
      currentUsage?: number;
      quota?: number;
      usagePercent?: number;
      availableSpace?: number;
      databases?: string[];
    };
    cookies: {
      count: number;
    };
    quota?: {
      usage: number;
      quota: number;
      usagePercent: number;
      availableSpace: number;
    };
  };

  // WebRTC Capabilities
  webrtc: {
    supported: boolean;
    stunServers?: string[];
    turnServers?: string[];
  };

  // Performance Metrics
  performance: {
    memory?: {
      usedJSHeapSize: number;
      totalJSHeapSize: number;
      jsHeapSizeLimit: number;
    };
    navigation: {
      type: number;
      redirectCount: number;
    };
    timing: {
      loadTime: number;
      domContentLoadedTime: number;
    };
  };

  // Feature Detection
  features: {
    webGL: boolean;
    webGL2: boolean;
    webWorkers: boolean;
    serviceWorkers: boolean;
    notifications: boolean;
    geolocation: boolean;
    webAudio: boolean;
    webSpeech: boolean;
    fullscreen: boolean;
    bluetooth: boolean;
    clipboard: boolean;
    wake_lock: boolean;
  };

  // Recording Test Results (merged into diagnosticData)
  recordingTest?: RecordingTestResults;
}

const StyledCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
}));

const InfoRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: theme.spacing(1, 0),
  '&:not(:last-child)': {
    borderBottom: '1px solid #f0f0f0',
  },
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  fontWeight: 600,
  color: theme.palette.primary.main,
}));

export const DiagnosticsView: React.FC = () => {
  const { getAccessToken } = useAccessToken();
  const [diagnosticData, setDiagnosticData] = useState<DiagnosticData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [testCompleted, setTestCompleted] = useState(false);
  const [recordingTestResults, setRecordingTestResults] = useState<RecordingTestResults | null>(
    null,
  );

  const collectDiagnosticData = useCallback(async () => {
    try {
      setLoading(true);

      // Collect browser information
      const browser = {
        userAgent: navigator.userAgent,
        platform: navigator.platform,
        language: navigator.language,
        languages: Array.from(navigator.languages || []),
        cookiesEnabled: navigator.cookieEnabled,
        onLine: navigator.onLine,
        vendor: navigator.vendor,
        appName: navigator.appName,
        appVersion: navigator.appVersion,
        product: navigator.product,
      };

      // Collect screen information
      const screen = {
        width: window.screen.width,
        height: window.screen.height,
        availWidth: window.screen.availWidth,
        availHeight: window.screen.availHeight,
        colorDepth: window.screen.colorDepth,
        pixelDepth: window.screen.pixelDepth,
        orientation: window.screen.orientation?.type,
        devicePixelRatio: window.devicePixelRatio,
      };

      // Collect window information
      const windowInfo = {
        innerWidth: window.innerWidth,
        innerHeight: window.innerHeight,
        outerWidth: window.outerWidth,
        outerHeight: window.outerHeight,
        scrollX: window.scrollX,
        scrollY: window.scrollY,
      };

      // Collect network information
      interface NetworkInformation {
        type?: string;
        effectiveType?: string;
        downlink?: number;
        downlinkMax?: number;
        rtt?: number;
        saveData?: boolean;
      }

      const nav = navigator as Navigator & {
        connection?: NetworkInformation;
        mozConnection?: NetworkInformation;
        webkitConnection?: NetworkInformation;
      };

      const connection = nav.connection || nav.mozConnection || nav.webkitConnection;
      const network: NetworkInformation = {
        type: connection?.type,
        effectiveType: connection?.effectiveType,
        downlink: connection?.downlink,
        downlinkMax: connection?.downlinkMax,
        rtt: connection?.rtt,
        saveData: connection?.saveData,
      };

      // Collect media devices
      const mediaDevices = await navigator.mediaDevices.enumerateDevices();
      const audioInputDevices = mediaDevices.filter(device => device.kind === 'audioinput');
      const audioOutputDevices = mediaDevices.filter(device => device.kind === 'audiooutput');
      const videoInputDevices = mediaDevices.filter(device => device.kind === 'videoinput');

      // Check media permissions
      let audioPermission: PermissionState | null = null;
      let videoPermission: PermissionState | null = null;

      if ('permissions' in navigator) {
        try {
          const audioStatus = await navigator.permissions.query({
            name: 'microphone' as PermissionName,
          });
          audioPermission = audioStatus.state;
        } catch {
          // Permission not supported
        }

        try {
          const videoStatus = await navigator.permissions.query({
            name: 'camera' as PermissionName,
          });
          videoPermission = videoStatus.state;
        } catch {
          // Permission not supported
        }
      }

      // Check audio/video codec support
      const supportedAudioCodecs = ['mp3', 'wav', 'ogg', 'webm', 'aac', 'flac', 'opus'].filter(
        codec => {
          const audio = document.createElement('audio');
          return audio.canPlayType(`audio/${codec}`) !== '';
        },
      );

      const supportedVideoCodecs = ['mp4', 'webm', 'ogg', 'mov'].filter(codec => {
        const video = document.createElement('video');
        return video.canPlayType(`video/${codec}`) !== '';
      });

      const media = {
        audioInputDevices,
        audioOutputDevices,
        videoInputDevices,
        mediaPermissions: {
          audio: audioPermission,
          video: videoPermission,
        },
        supportedAudioCodecs,
        supportedVideoCodecs,
      };

      // Collect system information
      const navWithDeviceMemory = navigator as Navigator & { deviceMemory?: number };
      const system = {
        hardwareConcurrency: navigator.hardwareConcurrency,
        deviceMemory: navWithDeviceMemory.deviceMemory,
        maxTouchPoints: navigator.maxTouchPoints,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        timezoneOffset: new Date().getTimezoneOffset(),
        dateTime: new Date().toISOString(),
      };

      // Collect storage information
      const localStorageAvailable = (() => {
        try {
          localStorage.setItem('test', 'test');
          localStorage.removeItem('test');
          return true;
        } catch {
          return false;
        }
      })();

      const sessionStorageAvailable = (() => {
        try {
          sessionStorage.setItem('test', 'test');
          sessionStorage.removeItem('test');
          return true;
        } catch {
          return false;
        }
      })();

      // Get estimated storage quota first
      let storageEstimate: StorageEstimate | null = null;
      if ('storage' in navigator && 'estimate' in navigator.storage) {
        try {
          storageEstimate = await navigator.storage.estimate();
        } catch (e) {
          console.error('Failed to get storage estimate:', e);
        }
      }

      // Calculate localStorage usage and estimate quota
      const localStorageUsage = localStorageAvailable
        ? new Blob(Object.values(localStorage)).size
        : undefined;

      // LocalStorage typically has a 5-10MB limit, we'll estimate conservatively
      const estimatedLocalStorageQuota = 5 * 1024 * 1024; // 5MB in bytes

      // Calculate sessionStorage usage
      const sessionStorageUsage = sessionStorageAvailable
        ? new Blob(Object.values(sessionStorage)).size
        : undefined;

      const storage: DiagnosticData['storage'] = {
        localStorage: {
          available: localStorageAvailable,
          keys: localStorageAvailable ? Object.keys(localStorage) : [],
          currentUsage: localStorageUsage,
          quota: localStorageAvailable ? estimatedLocalStorageQuota : undefined,
          usagePercent:
            localStorageUsage && localStorageAvailable
              ? Math.round((localStorageUsage / estimatedLocalStorageQuota) * 100)
              : undefined,
        },
        sessionStorage: {
          available: sessionStorageAvailable,
          keys: sessionStorageAvailable ? Object.keys(sessionStorage) : [],
          currentUsage: sessionStorageUsage,
          quota: sessionStorageAvailable ? estimatedLocalStorageQuota : undefined,
          usagePercent:
            sessionStorageUsage && sessionStorageAvailable
              ? Math.round((sessionStorageUsage / estimatedLocalStorageQuota) * 100)
              : undefined,
        },
        indexedDB: {
          available: 'indexedDB' in window,
          currentUsage: storageEstimate?.usage,
          quota: storageEstimate?.quota,
          usagePercent:
            storageEstimate?.usage && storageEstimate?.quota
              ? Math.round((storageEstimate.usage / storageEstimate.quota) * 100)
              : undefined,
          availableSpace:
            storageEstimate?.usage && storageEstimate?.quota
              ? storageEstimate.quota - storageEstimate.usage
              : undefined,
        },
        cookies: {
          count: document.cookie.split(';').filter(c => c.trim()).length,
        },
      };

      // Add overall storage quota info if available
      if (storageEstimate?.usage !== undefined && storageEstimate?.quota !== undefined) {
        storage.quota = {
          usage: storageEstimate.usage,
          quota: storageEstimate.quota,
          usagePercent: Math.round((storageEstimate.usage / storageEstimate.quota) * 100),
          availableSpace: storageEstimate.quota - storageEstimate.usage,
        };
      }

      // Check WebRTC support
      const webrtc = {
        supported: 'RTCPeerConnection' in window,
      };

      // Collect performance metrics
      const performanceData: DiagnosticData['performance'] = {
        navigation: {
          type: performance.navigation.type,
          redirectCount: performance.navigation.redirectCount,
        },
        timing: {
          loadTime: performance.timing.loadEventEnd - performance.timing.navigationStart,
          domContentLoadedTime:
            performance.timing.domContentLoadedEventEnd - performance.timing.navigationStart,
        },
      };

      interface PerformanceMemory {
        usedJSHeapSize: number;
        totalJSHeapSize: number;
        jsHeapSizeLimit: number;
      }

      if ('memory' in performance) {
        const perfWithMemory = performance as Performance & { memory: PerformanceMemory };
        performanceData.memory = {
          usedJSHeapSize: perfWithMemory.memory.usedJSHeapSize,
          totalJSHeapSize: perfWithMemory.memory.totalJSHeapSize,
          jsHeapSizeLimit: perfWithMemory.memory.jsHeapSizeLimit,
        };
      }

      // Feature detection
      const features = {
        webGL: (() => {
          try {
            const canvas = document.createElement('canvas');
            return !!(
              window.WebGLRenderingContext &&
              (canvas.getContext('webgl') || canvas.getContext('experimental-webgl'))
            );
          } catch {
            return false;
          }
        })(),
        webGL2: (() => {
          try {
            const canvas = document.createElement('canvas');
            return !!canvas.getContext('webgl2');
          } catch {
            return false;
          }
        })(),
        webWorkers: 'Worker' in window,
        serviceWorkers: 'serviceWorker' in navigator,
        notifications: 'Notification' in window,
        geolocation: 'geolocation' in navigator,
        webAudio: 'AudioContext' in window || 'webkitAudioContext' in window,
        webSpeech: 'speechSynthesis' in window,
        fullscreen: 'fullscreenEnabled' in document || 'webkitFullscreenEnabled' in document,
        bluetooth: 'bluetooth' in navigator,
        clipboard: 'clipboard' in navigator,
        wake_lock: 'wakeLock' in navigator,
      };

      setDiagnosticData({
        browser,
        screen,
        window: windowInfo,
        network,
        media,
        system,
        storage,
        webrtc,
        performance: performanceData,
        features,
        recordingTest: recordingTestResults || undefined,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to collect diagnostic data');
    } finally {
      setLoading(false);
    }
  }, [recordingTestResults]);

  useEffect(() => {
    // Don't collect diagnostics until after the test is completed
    if (testCompleted) {
      collectDiagnosticData();
    }
  }, [testCompleted, collectDiagnosticData]);

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  };

  const sendDiagnosticsToBackend = async () => {
    if (!diagnosticData) {
      console.error('Missing diagnostic data');
      return;
    }

    try {
      const accessToken = await getAccessToken();

      // Send diagnosticData directly as the payload - it already includes recordingTest
      // Debug: Log diagnostics being sent
      // eslint-disable-next-line no-console
      console.log('Sending diagnostics to backend:', diagnosticData);

      // TODO: Replace with actual backend endpoint
      const response = await APIService.makeAPIPostRequest({
        requestString: '/v2/provider/events/diagnostics/submit',
        accessToken,
        body: {
          diagnostic_data: diagnosticData,
        },
      });

      if (response.ok) {
        // eslint-disable-next-line no-console
        console.log('Diagnostics sent successfully');
      } else {
        console.error('Failed to send diagnostics:', response.error);
      }
    } catch (error) {
      console.error('Error sending diagnostics:', error);
    }
  };

  const handleTestComplete = (results: RecordingTestResults) => {
    setRecordingTestResults(results);
    setTestCompleted(true);
  };

  useEffect(() => {
    // Send diagnostics once we have diagnostic data with recording test results
    if (diagnosticData && diagnosticData.recordingTest) {
      sendDiagnosticsToBackend();
    }
  }, [diagnosticData]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          System Diagnostics
        </Typography>
        <LinearProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          System Diagnostics
        </Typography>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
        System Diagnostics
      </Typography>

      {/* Storage Management Section */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12}>
          <StorageManagementCard />
        </Grid>
      </Grid>

      {/* Show recording test first */}
      {!testCompleted && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TestRecordingWidget onTestComplete={handleTestComplete} />
          </Grid>
        </Grid>
      )}

      {/* Show diagnostics only after test is completed */}
      {testCompleted && diagnosticData && (
        <>
          <Grid container spacing={3}>
            {/* Browser Information */}
            <Grid item xs={12} md={6}>
              <StyledCard>
                <CardContent>
                  <SectionTitle variant="h6">Browser Information</SectionTitle>
                  <InfoRow>
                    <Typography variant="body2" color="textSecondary">
                      User Agent
                    </Typography>
                    <Typography variant="body2" sx={{ maxWidth: '60%', wordBreak: 'break-word' }}>
                      {diagnosticData.browser.userAgent}
                    </Typography>
                  </InfoRow>
                  <InfoRow>
                    <Typography variant="body2" color="textSecondary">
                      Platform
                    </Typography>
                    <Typography variant="body2">{diagnosticData.browser.platform}</Typography>
                  </InfoRow>
                  <InfoRow>
                    <Typography variant="body2" color="textSecondary">
                      Language
                    </Typography>
                    <Typography variant="body2">{diagnosticData.browser.language}</Typography>
                  </InfoRow>
                  <InfoRow>
                    <Typography variant="body2" color="textSecondary">
                      Cookies Enabled
                    </Typography>
                    <Chip
                      label={diagnosticData.browser.cookiesEnabled ? 'Yes' : 'No'}
                      color={diagnosticData.browser.cookiesEnabled ? 'success' : 'error'}
                      size="small"
                    />
                  </InfoRow>
                  <InfoRow>
                    <Typography variant="body2" color="textSecondary">
                      Online Status
                    </Typography>
                    <Chip
                      label={diagnosticData.browser.onLine ? 'Online' : 'Offline'}
                      color={diagnosticData.browser.onLine ? 'success' : 'error'}
                      size="small"
                    />
                  </InfoRow>
                </CardContent>
              </StyledCard>
            </Grid>

            {/* Screen Information */}
            <Grid item xs={12} md={6}>
              <StyledCard>
                <CardContent>
                  <SectionTitle variant="h6">Screen Information</SectionTitle>
                  <InfoRow>
                    <Typography variant="body2" color="textSecondary">
                      Resolution
                    </Typography>
                    <Typography variant="body2">
                      {diagnosticData.screen.width} × {diagnosticData.screen.height}
                    </Typography>
                  </InfoRow>
                  <InfoRow>
                    <Typography variant="body2" color="textSecondary">
                      Available Space
                    </Typography>
                    <Typography variant="body2">
                      {diagnosticData.screen.availWidth} × {diagnosticData.screen.availHeight}
                    </Typography>
                  </InfoRow>
                  <InfoRow>
                    <Typography variant="body2" color="textSecondary">
                      Color Depth
                    </Typography>
                    <Typography variant="body2">{diagnosticData.screen.colorDepth} bits</Typography>
                  </InfoRow>
                  <InfoRow>
                    <Typography variant="body2" color="textSecondary">
                      Pixel Ratio
                    </Typography>
                    <Typography variant="body2">
                      {diagnosticData.screen.devicePixelRatio}
                    </Typography>
                  </InfoRow>
                  {diagnosticData.screen.orientation && (
                    <InfoRow>
                      <Typography variant="body2" color="textSecondary">
                        Orientation
                      </Typography>
                      <Typography variant="body2">{diagnosticData.screen.orientation}</Typography>
                    </InfoRow>
                  )}
                </CardContent>
              </StyledCard>
            </Grid>

            {/* Window Information */}
            <Grid item xs={12} md={6}>
              <StyledCard>
                <CardContent>
                  <SectionTitle variant="h6">Window Information</SectionTitle>
                  <InfoRow>
                    <Typography variant="body2" color="textSecondary">
                      Viewport Size
                    </Typography>
                    <Typography variant="body2">
                      {diagnosticData.window.innerWidth} × {diagnosticData.window.innerHeight}
                    </Typography>
                  </InfoRow>
                  <InfoRow>
                    <Typography variant="body2" color="textSecondary">
                      Window Size
                    </Typography>
                    <Typography variant="body2">
                      {diagnosticData.window.outerWidth} × {diagnosticData.window.outerHeight}
                    </Typography>
                  </InfoRow>
                  <InfoRow>
                    <Typography variant="body2" color="textSecondary">
                      Scroll Position
                    </Typography>
                    <Typography variant="body2">
                      X: {diagnosticData.window.scrollX}, Y: {diagnosticData.window.scrollY}
                    </Typography>
                  </InfoRow>
                </CardContent>
              </StyledCard>
            </Grid>

            {/* Network Information */}
            <Grid item xs={12} md={6}>
              <StyledCard>
                <CardContent>
                  <SectionTitle variant="h6">Network Information</SectionTitle>
                  {diagnosticData.network.effectiveType ? (
                    <>
                      <InfoRow>
                        <Typography variant="body2" color="textSecondary">
                          Connection Type
                        </Typography>
                        <Typography variant="body2">
                          {diagnosticData.network.effectiveType}
                        </Typography>
                      </InfoRow>
                      {diagnosticData.network.downlink && (
                        <InfoRow>
                          <Typography variant="body2" color="textSecondary">
                            Downlink
                          </Typography>
                          <Typography variant="body2">
                            {diagnosticData.network.downlink} Mbps
                          </Typography>
                        </InfoRow>
                      )}
                      {diagnosticData.network.rtt && (
                        <InfoRow>
                          <Typography variant="body2" color="textSecondary">
                            Round Trip Time
                          </Typography>
                          <Typography variant="body2">{diagnosticData.network.rtt} ms</Typography>
                        </InfoRow>
                      )}
                      <InfoRow>
                        <Typography variant="body2" color="textSecondary">
                          Data Saver
                        </Typography>
                        <Chip
                          label={diagnosticData.network.saveData ? 'Enabled' : 'Disabled'}
                          color={diagnosticData.network.saveData ? 'warning' : 'default'}
                          size="small"
                        />
                      </InfoRow>
                    </>
                  ) : (
                    <Typography variant="body2" color="textSecondary">
                      Network information not available
                    </Typography>
                  )}
                </CardContent>
              </StyledCard>
            </Grid>

            {/* Media Devices */}
            <Grid item xs={12}>
              <StyledCard>
                <CardContent>
                  <SectionTitle variant="h6">Media Devices</SectionTitle>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <Typography variant="subtitle2" gutterBottom>
                        Audio Input Devices
                      </Typography>
                      {diagnosticData.media.audioInputDevices.map((device, index) => (
                        <Box key={index} sx={{ mb: 1 }}>
                          <Typography variant="body2" noWrap>
                            {device.label || `Microphone ${index + 1}`}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {device.deviceId.substring(0, 8)}...
                          </Typography>
                        </Box>
                      ))}
                      {diagnosticData.media.audioInputDevices.length === 0 && (
                        <Typography variant="body2" color="textSecondary">
                          None detected
                        </Typography>
                      )}
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <Typography variant="subtitle2" gutterBottom>
                        Audio Output Devices
                      </Typography>
                      {diagnosticData.media.audioOutputDevices.map((device, index) => (
                        <Box key={index} sx={{ mb: 1 }}>
                          <Typography variant="body2" noWrap>
                            {device.label || `Speaker ${index + 1}`}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {device.deviceId.substring(0, 8)}...
                          </Typography>
                        </Box>
                      ))}
                      {diagnosticData.media.audioOutputDevices.length === 0 && (
                        <Typography variant="body2" color="textSecondary">
                          None detected
                        </Typography>
                      )}
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <Typography variant="subtitle2" gutterBottom>
                        Video Input Devices
                      </Typography>
                      {diagnosticData.media.videoInputDevices.map((device, index) => (
                        <Box key={index} sx={{ mb: 1 }}>
                          <Typography variant="body2" noWrap>
                            {device.label || `Camera ${index + 1}`}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {device.deviceId.substring(0, 8)}...
                          </Typography>
                        </Box>
                      ))}
                      {diagnosticData.media.videoInputDevices.length === 0 && (
                        <Typography variant="body2" color="textSecondary">
                          None detected
                        </Typography>
                      )}
                    </Grid>
                  </Grid>

                  <Divider sx={{ my: 2 }} />

                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" gutterBottom>
                        Media Permissions
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                        <Chip
                          label={`Microphone: ${diagnosticData.media.mediaPermissions.audio || 'Unknown'}`}
                          color={
                            diagnosticData.media.mediaPermissions.audio === 'granted'
                              ? 'success'
                              : 'default'
                          }
                          size="small"
                        />
                        <Chip
                          label={`Camera: ${diagnosticData.media.mediaPermissions.video || 'Unknown'}`}
                          color={
                            diagnosticData.media.mediaPermissions.video === 'granted'
                              ? 'success'
                              : 'default'
                          }
                          size="small"
                        />
                      </Box>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" gutterBottom>
                        Supported Codecs
                      </Typography>
                      <Box sx={{ mb: 1 }}>
                        <Typography variant="caption" color="textSecondary">
                          Audio:
                        </Typography>
                        <Typography variant="body2">
                          {diagnosticData.media.supportedAudioCodecs.join(', ')}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="textSecondary">
                          Video:
                        </Typography>
                        <Typography variant="body2">
                          {diagnosticData.media.supportedVideoCodecs.join(', ')}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </StyledCard>
            </Grid>

            {/* System Information */}
            <Grid item xs={12} md={6}>
              <StyledCard>
                <CardContent>
                  <SectionTitle variant="h6">System Information</SectionTitle>
                  <InfoRow>
                    <Typography variant="body2" color="textSecondary">
                      CPU Cores
                    </Typography>
                    <Typography variant="body2">
                      {diagnosticData.system.hardwareConcurrency}
                    </Typography>
                  </InfoRow>
                  {diagnosticData.system.deviceMemory && (
                    <InfoRow>
                      <Typography variant="body2" color="textSecondary">
                        Device Memory
                      </Typography>
                      <Typography variant="body2">
                        {diagnosticData.system.deviceMemory} GB
                      </Typography>
                    </InfoRow>
                  )}
                  <InfoRow>
                    <Typography variant="body2" color="textSecondary">
                      Touch Points
                    </Typography>
                    <Typography variant="body2">{diagnosticData.system.maxTouchPoints}</Typography>
                  </InfoRow>
                  <InfoRow>
                    <Typography variant="body2" color="textSecondary">
                      Timezone
                    </Typography>
                    <Typography variant="body2">{diagnosticData.system.timezone}</Typography>
                  </InfoRow>
                  <InfoRow>
                    <Typography variant="body2" color="textSecondary">
                      Current Time
                    </Typography>
                    <Typography variant="body2">
                      {new Date(diagnosticData.system.dateTime).toLocaleString()}
                    </Typography>
                  </InfoRow>
                </CardContent>
              </StyledCard>
            </Grid>

            {/* Storage Information */}
            <Grid item xs={12} md={6}>
              <StyledCard>
                <CardContent>
                  <SectionTitle variant="h6">Storage Information</SectionTitle>

                  {/* LocalStorage */}
                  <InfoRow>
                    <Typography variant="body2" color="textSecondary">
                      LocalStorage
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {diagnosticData.storage.localStorage.available ? (
                        <>
                          {diagnosticData.storage.localStorage.currentUsage !== undefined &&
                            diagnosticData.storage.localStorage.quota !== undefined && (
                              <>
                                <Chip
                                  label={`${diagnosticData.storage.localStorage.usagePercent}% used`}
                                  color={
                                    (diagnosticData.storage.localStorage.usagePercent ?? 0) > 90
                                      ? 'error'
                                      : (diagnosticData.storage.localStorage.usagePercent ?? 0) > 70
                                        ? 'warning'
                                        : 'success'
                                  }
                                  size="small"
                                />
                                <Typography variant="caption">
                                  {formatBytes(diagnosticData.storage.localStorage.currentUsage)} /{' '}
                                  {formatBytes(diagnosticData.storage.localStorage.quota)}
                                </Typography>
                              </>
                            )}
                          <Typography variant="caption" color="textSecondary">
                            ({diagnosticData.storage.localStorage.keys.length} keys)
                          </Typography>
                        </>
                      ) : (
                        <Chip label="Not Available" color="error" size="small" />
                      )}
                    </Box>
                  </InfoRow>

                  {/* SessionStorage */}
                  <InfoRow>
                    <Typography variant="body2" color="textSecondary">
                      SessionStorage
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {diagnosticData.storage.sessionStorage.available ? (
                        <>
                          {diagnosticData.storage.sessionStorage.currentUsage !== undefined &&
                            diagnosticData.storage.sessionStorage.quota !== undefined && (
                              <>
                                <Chip
                                  label={`${diagnosticData.storage.sessionStorage.usagePercent}% used`}
                                  color={
                                    (diagnosticData.storage.sessionStorage.usagePercent ?? 0) > 90
                                      ? 'error'
                                      : (diagnosticData.storage.sessionStorage.usagePercent ?? 0) >
                                          70
                                        ? 'warning'
                                        : 'success'
                                  }
                                  size="small"
                                />
                                <Typography variant="caption">
                                  {formatBytes(diagnosticData.storage.sessionStorage.currentUsage)}{' '}
                                  / {formatBytes(diagnosticData.storage.sessionStorage.quota)}
                                </Typography>
                              </>
                            )}
                          <Typography variant="caption" color="textSecondary">
                            ({diagnosticData.storage.sessionStorage.keys.length} keys)
                          </Typography>
                        </>
                      ) : (
                        <Chip label="Not Available" color="error" size="small" />
                      )}
                    </Box>
                  </InfoRow>

                  {/* IndexedDB */}
                  <InfoRow>
                    <Typography variant="body2" color="textSecondary">
                      IndexedDB
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      {diagnosticData.storage.indexedDB.available ? (
                        <>
                          {diagnosticData.storage.indexedDB.currentUsage !== undefined &&
                          diagnosticData.storage.indexedDB.quota !== undefined ? (
                            <>
                              <Chip
                                label={`${diagnosticData.storage.indexedDB.usagePercent}% used`}
                                color={
                                  (diagnosticData.storage.indexedDB.usagePercent ?? 0) > 90
                                    ? 'error'
                                    : (diagnosticData.storage.indexedDB.usagePercent ?? 0) > 70
                                      ? 'warning'
                                      : 'success'
                                }
                                size="small"
                              />
                              <Typography variant="caption">
                                {formatBytes(diagnosticData.storage.indexedDB.currentUsage)} /{' '}
                                {formatBytes(diagnosticData.storage.indexedDB.quota)}
                              </Typography>
                              {diagnosticData.storage.indexedDB.availableSpace !== undefined && (
                                <Typography variant="caption" color="textSecondary">
                                  ({formatBytes(diagnosticData.storage.indexedDB.availableSpace)}{' '}
                                  available)
                                </Typography>
                              )}
                            </>
                          ) : (
                            <Chip label="Available" color="success" size="small" />
                          )}
                        </>
                      ) : (
                        <Chip label="Not Available" color="error" size="small" />
                      )}
                    </Box>
                  </InfoRow>

                  {/* Overall Storage Quota */}
                  {diagnosticData.storage.quota && (
                    <>
                      <Divider sx={{ my: 1 }} />
                      <InfoRow>
                        <Typography variant="body2" color="textSecondary">
                          Total Storage
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip
                            label={`${diagnosticData.storage.quota.usagePercent}% used`}
                            color={
                              diagnosticData.storage.quota.usagePercent > 90
                                ? 'error'
                                : diagnosticData.storage.quota.usagePercent > 70
                                  ? 'warning'
                                  : 'success'
                            }
                            size="small"
                          />
                          <Typography variant="caption">
                            {formatBytes(diagnosticData.storage.quota.usage)} /{' '}
                            {formatBytes(diagnosticData.storage.quota.quota)}
                          </Typography>
                        </Box>
                      </InfoRow>
                      <InfoRow>
                        <Typography variant="body2" color="textSecondary">
                          Available Space
                        </Typography>
                        <Typography variant="body2">
                          {formatBytes(diagnosticData.storage.quota.availableSpace)}
                        </Typography>
                      </InfoRow>
                      {diagnosticData.storage.quota.quota < 100 * 1024 * 1024 && ( // Less than 100MB
                        <Alert severity="warning" sx={{ mt: 1 }}>
                          Low storage quota detected. This may cause issues with recording.
                        </Alert>
                      )}
                    </>
                  )}

                  <InfoRow>
                    <Typography variant="body2" color="textSecondary">
                      Cookies
                    </Typography>
                    <Typography variant="body2">
                      {diagnosticData.storage.cookies.count} cookies
                    </Typography>
                  </InfoRow>
                </CardContent>
              </StyledCard>
            </Grid>

            {/* Performance Metrics */}
            <Grid item xs={12} md={6}>
              <StyledCard>
                <CardContent>
                  <SectionTitle variant="h6">Performance Metrics</SectionTitle>
                  <InfoRow>
                    <Typography variant="body2" color="textSecondary">
                      Page Load Time
                    </Typography>
                    <Typography variant="body2">
                      {diagnosticData.performance.timing.loadTime} ms
                    </Typography>
                  </InfoRow>
                  <InfoRow>
                    <Typography variant="body2" color="textSecondary">
                      DOM Content Loaded
                    </Typography>
                    <Typography variant="body2">
                      {diagnosticData.performance.timing.domContentLoadedTime} ms
                    </Typography>
                  </InfoRow>
                  {diagnosticData.performance.memory && (
                    <>
                      <InfoRow>
                        <Typography variant="body2" color="textSecondary">
                          JS Heap Used
                        </Typography>
                        <Typography variant="body2">
                          {formatBytes(diagnosticData.performance.memory.usedJSHeapSize)}
                        </Typography>
                      </InfoRow>
                      <InfoRow>
                        <Typography variant="body2" color="textSecondary">
                          JS Heap Total
                        </Typography>
                        <Typography variant="body2">
                          {formatBytes(diagnosticData.performance.memory.totalJSHeapSize)}
                        </Typography>
                      </InfoRow>
                      <InfoRow>
                        <Typography variant="body2" color="textSecondary">
                          JS Heap Limit
                        </Typography>
                        <Typography variant="body2">
                          {formatBytes(diagnosticData.performance.memory.jsHeapSizeLimit)}
                        </Typography>
                      </InfoRow>
                    </>
                  )}
                </CardContent>
              </StyledCard>
            </Grid>

            {/* Feature Support */}
            <Grid item xs={12} md={6}>
              <StyledCard>
                <CardContent>
                  <SectionTitle variant="h6">Feature Support</SectionTitle>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {Object.entries(diagnosticData.features).map(([feature, supported]) => (
                      <Chip
                        key={feature}
                        label={feature.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        color={supported ? 'success' : 'default'}
                        size="small"
                        variant={supported ? 'filled' : 'outlined'}
                      />
                    ))}
                  </Box>
                </CardContent>
              </StyledCard>
            </Grid>

            {/* Recording Test Results */}
            {diagnosticData.recordingTest && (
              <Grid item xs={12}>
                <StyledCard>
                  <CardContent>
                    <SectionTitle variant="h6">Recording Test Results</SectionTitle>
                    <Box sx={{ mb: 2 }}>
                      <Chip
                        label={
                          diagnosticData.recordingTest.success
                            ? 'All Tests Passed'
                            : 'Some Tests Failed'
                        }
                        color={diagnosticData.recordingTest.success ? 'success' : 'error'}
                        sx={{ mb: 2 }}
                      />
                    </Box>

                    {diagnosticData.recordingTest.steps &&
                      diagnosticData.recordingTest.steps.map((step, index) => (
                        <Box key={step.id} sx={{ mb: 2 }}>
                          <InfoRow>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body2" color="textSecondary">
                                {index + 1}. {step.label}
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Chip
                                label={step.status}
                                color={
                                  step.status === 'success'
                                    ? 'success'
                                    : step.status === 'error'
                                      ? 'error'
                                      : 'default'
                                }
                                size="small"
                              />
                              {step.duration && (
                                <Typography variant="caption" color="textSecondary">
                                  {step.duration}ms
                                </Typography>
                              )}
                            </Box>
                          </InfoRow>
                          {step.error && (
                            <Typography variant="body2" color="error" sx={{ ml: 3, mt: 0.5 }}>
                              {step.error}
                            </Typography>
                          )}
                          {step.details && (
                            <Box sx={{ ml: 3, mt: 1, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                              <Typography
                                variant="caption"
                                component="pre"
                                sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}
                              >
                                {JSON.stringify(step.details, null, 2)}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      ))}

                    <Divider sx={{ my: 2 }} />
                    <Typography variant="caption" color="textSecondary">
                      Test completed at:{' '}
                      {new Date(diagnosticData.recordingTest.timestamp).toLocaleString()}
                    </Typography>
                  </CardContent>
                </StyledCard>
              </Grid>
            )}
          </Grid>
        </>
      )}
    </Box>
  );
};
